import React, { Fragment, useEffect } from "react";
import Styles from "./style.module.scss";
import Packets from "../../components/Packets/Packets";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getArrPackets, getPacketsCategories, handlerErrorGetArrPackets } from "../../redux/Reducers/packetSlice";
import { useLocation } from "react-router-dom";
import PreLoader from "../../components/PreLoader/PreLoader";
import { MetaData } from "../../helpers/MetaData";
import { framerMotionPhotosAndPackets, hiddenAndScale, hiddenUnderLine } from "../../helpers/framerMotion";
import { motion } from "framer-motion";
import { useWindowResize } from "../../hooks/useWindowResize";
import JsonLd from "../../helpers/JsonLD";
import NoteImportantPrices from "./components/NoteImportantPrices";
import NewbornPricingPage from "./components/NewbornPricingPage";
import FamilyMaternityPricingPage from "./components/FamilyMaternityPricingPage";
import { charlottePhotographyServiceAreas } from "../../utils/seo";

const Prices: React.FC = () => {
  const dispatch = useAppDispatch();
  const { getPackets, packetsCategories, loading, error } = useAppSelector((state) => state.packets);
  const { pathname } = useLocation();
  const isNewbornPricing = pathname.includes("/prices/newborn");
  const publicPhotosessionType = pathname.split("/").filter(Boolean).pop() || "";
  const isPortraitStaticPricing = publicPhotosessionType === "baby" || publicPhotosessionType === "family" || publicPhotosessionType === "maternity";
  const isStaticPricing = isNewbornPricing || isPortraitStaticPricing;
  const apiPhotosessionType = publicPhotosessionType;
  const typePhotosession = publicPhotosessionType;
  const canonicalPath = pathname.endsWith("/") ? pathname : `${pathname}/`;

  useEffect(() => {
    // TODO: Reconnect the static Investment pages to the package API and admin editor.
    // Keep this branch intact: it remains the server-backed fallback for non-static pricing categories.
    if (!isStaticPricing) {
      dispatch(getPacketsCategories());
    }
  }, [dispatch, isStaticPricing]);

  useEffect(() => {
    if (isStaticPricing) return;

    if (packetsCategories.length > 0) {
      const categoryExists = packetsCategories.some((category) => category.title === apiPhotosessionType);

      if (categoryExists) {
        dispatch(getArrPackets({ photosessionType: apiPhotosessionType }));
      } else {
        dispatch(handlerErrorGetArrPackets("Error, the package has not been uploaded!"));
      }
    } else {
      dispatch(handlerErrorGetArrPackets("Error, the package has not been uploaded!"));
    }
  }, [apiPhotosessionType, packetsCategories, dispatch, isStaticPricing]);

  const { width } = useWindowResize();
  const { container, item } = framerMotionPhotosAndPackets();

  if (isNewbornPricing) {
    return <NewbornPricingPage />;
  }

  if (isPortraitStaticPricing) {
    return <FamilyMaternityPricingPage type={publicPhotosessionType as "baby" | "family" | "maternity"} />;
  }

  const sortPackets = [...getPackets].sort((a, b) => Number(a.price) - Number(b.price));

  const UpOneLetterPhotoSession =
    typePhotosession.length > 1 && typePhotosession[0].toUpperCase() + typePhotosession.slice(1);

  const webPageDataPrices = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `https://alenalobacheva.com${canonicalPath}#webpage-prices`,
    name: `Prices for ${UpOneLetterPhotoSession} photography | Charlotte ${UpOneLetterPhotoSession} Photographer, Alena Lobacheva`,
    description: `Discover the investment details for ${UpOneLetterPhotoSession} Photography: explore prices, packages, and conditions. Book your session with Alena Lobacheva Photography in North Carolina.`,
    image: `https://cdn.alenalobacheva.com/gallery/${typePhotosession}/${typePhotosession}3.webp`,
    url: `https://alenalobacheva.com${canonicalPath}`,
    potentialAction: {
      "@type": "ReadAction",
    },
  };

  const localBusinessDataPrices = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://alenalobacheva.com${canonicalPath}#localbusiness-prices`,
    name: `Charlotte ${UpOneLetterPhotoSession} Photographer, Alena Lobacheva`,
    description: `Discover the investment details for ${UpOneLetterPhotoSession} Photography: explore prices, packages, and conditions. Book your session with Alena Lobacheva Photography in Charlotte, NC.`,
    image: `https://cdn.alenalobacheva.com/gallery/${typePhotosession}/${typePhotosession}3.webp`,
    url: `https://alenalobacheva.com${canonicalPath}`,
    telephone: "+1-516-468-4837",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Charlotte",
      addressRegion: "NC",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-516-468-4837",
      contactType: "customer support",
    },
    areaServed: charlottePhotographyServiceAreas,
    sameAs: [
      "https://www.instagram.com/lobachevaphotography/",
      "https://www.facebook.com/Alen4ikLobacheva?mibextid=9R9pXO",
      "https://www.tiktok.com/@lobachevaphotography/",
    ],
  };

  const serviceDataPrices = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://alenalobacheva.com${canonicalPath}#service-prices`,
    name: `${UpOneLetterPhotoSession} Photography Sessions`,
    description: `Offering professional ${UpOneLetterPhotoSession} Photography sessions in Charlotte, Huntersville, Ballantyne, Weddington.`,
    serviceType: "Photography",
    areaServed: charlottePhotographyServiceAreas,
  };

  const pricingTitles:Record<string, string> = {
    Newborn: "Newborn Photography Pricing and Session Packages",
    Baby: "Baby Photography Session Pricing and Packages",
    Family: "Family Photography Session Packages and Pricing",
    Maternity: "Maternity Photography Session Packages and Pricing",
  };

  const pageTitle = pricingTitles[UpOneLetterPhotoSession as keyof typeof pricingTitles];

  return (
    <Fragment>
      <JsonLd data={webPageDataPrices} />
      <JsonLd data={localBusinessDataPrices} />
      <JsonLd data={serviceDataPrices} />
      <MetaData
        title={`Prices for ${UpOneLetterPhotoSession} Photography`}
        description={`Discover the investment details for ${UpOneLetterPhotoSession} Photography: explore prices, packages, and conditions. Book your session with Alena Lobacheva Photography in Charlotte, NC`}
        canonicalLink={`https://alenalobacheva.com${canonicalPath}`}
        imageAltOG={`${UpOneLetterPhotoSession} Photography`}
        imageOG={`https://cdn.alenalobacheva.com/gallery/${typePhotosession}/${typePhotosession}3.webp`}
        titleOG={`Prices for ${UpOneLetterPhotoSession} Photography`}
        descriptionOG={`Discover the investment details for ${UpOneLetterPhotoSession} Photography: explore prices, packages, and conditions. Book your session with Alena Lobacheva Photography in Charlotte, NC`}
      />
      <motion.section className={Styles.prices} initial="hidden" animate="visible">
        {pageTitle && (
          <motion.h1 variants={hiddenAndScale} custom={1} className={Styles.prices__title}>
            {pageTitle}
          </motion.h1>
        )}
        {!loading.getArrPackets && (
          <motion.div
            className={Styles.prices__titleUnderLine}
            variants={hiddenUnderLine}
            custom={{ delay: 2, widthFull: width > 567 ? 500 : 320 }}
          />
        )}
        <div className={Styles.prices__container}>
          {loading.getArrPackets ? (
            <div style={{ gridColumn: "1/-1", marginTop: "50px" }}>
              <PreLoader />
            </div>
          ) : (
            (
              <>
                <Packets
                  getPackets={sortPackets}
                  item={item}
                  container={container}
                  styleContainer={Styles.prices__packets}
                />
              </>
            ) ||
            (error.packets && <p style={{ gridColumn: "1/-1" }}>{error.packets}</p>)
          )}
        </div>
        {getPackets.length > 0 && <NoteImportantPrices />}
      </motion.section>
    </Fragment>
  );
};

export default Prices;
